/**
 * ═══════════════════════════════════════════════════════════════════════════
 * INVOICE OPERATIONS (UPDATED FOR NEW SCHEMA)
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Manages invoice CRUD operations with:
 * - Embedded snapshots (brand/settings as JSONB)
 * - Embedded line items (lines as JSONB array)
 * - ACID transactions (PostgreSQL RPC functions)
 * - Edit workflow with replacement invoices
 * - Payment transfer system
 * - Optimistic locking with version control
 * - Cascade soft delete/restore
 * - Data validation (Zod schemas)
 * - Multi-user isolation (RLS)
 *
 * SCHEMA CHANGES:
 * - OLD: brand_snapshot_id → brand_snapshots table → JOINs
 * - NEW: brand_snapshot JSONB column (embedded, frozen copy)
 * 
 * - OLD: settings_snapshot_id → settings_snapshots table → JOINs
 * - NEW: settings_snapshot JSONB column (embedded, frozen copy)
 * 
 * - OLD: invoice_lines table (separate rows) → JOINs
 * - NEW: lines JSONB array (embedded in invoice row)
 *
 * PERFORMANCE IMPROVEMENTS:
 * - ONE database query to fetch complete invoice (was 3-5 queries)
 * - No JOINs needed (everything embedded)
 * - Simpler code, faster execution
 *
 * SOLVED ISSUES:
 * - Issue #1: ACID Transactions ✓ PostgreSQL RPC functions (Phase 2)
 * - Issue #2: Counter Before Operation ✓ RPC handles atomically
 * - Issue #3: Multi-Step Not Atomic ✓ Complete workflow in RPC
 * - Issue #4: Export Not Atomic ✓ Single row reads (no JOINs)
 * - Issue #5: Lock Timeout Race ✓ PostgreSQL row locking in RPC
 * - Issue #6: Bulk Partial Failure ✓ RPC transactions handle failures
 *
 * ═══════════════════════════════════════════════════════════════════════════
 */

import { supabase, getCurrentUserId } from "../core/supabase.js";
import { invoiceSchema, validateOrThrow, createValidationError } from "../validation/schemas.js";
import { requireFeature, requireLimit } from '../core/permissions.js';
import { FEATURES, LIMITS } from '../features/featureList.js';

// ══════════════════════════════════════════════════════════════════════════════
// INVOICE CREATION
// ══════════════════════════════════════════════════════════════════════════════

/**
 * Create invoice with full validation and ACID transaction
 *
 * ✅ UPDATED FOR NEW SCHEMA:
 * - Fetches current brand/settings (no snapshot IDs)
 * - Embeds brand/settings as JSONB in invoice
 * - Embeds line items as JSONB array
 * - Single RPC call creates complete invoice atomically
 *
 * @param {object} invoiceData - Invoice data from UI
 * @returns {Promise<object>} { success: boolean, invoice?: object, error?: string }
 */
export async function createInvoice(invoiceData) {
  // ✅ PERMISSION CHECKS
  await requireFeature(FEATURES.CLOUD_DATABASE);
  await requireLimit(LIMITS.INVOICES_PER_MONTH);
  
  try {
    // ────────────────────────────────────────────────────────────────────────
    // STEP 1: VALIDATE INPUT DATA
    // ────────────────────────────────────────────────────────────────────────
    
    const validatedData = validateOrThrow(invoiceSchema, invoiceData);
    
    // ────────────────────────────────────────────────────────────────────────
    // STEP 2: GET CURRENT BRAND AND SETTINGS
    // ────────────────────────────────────────────────────────────────────────
    
    const { getCurrentBrandInfo } = await import("./brand.js");
    const { getCurrentSettings } = await import("./settings.js");
    
    const brandInfo = await getCurrentBrandInfo({ hydrate: false });
    const settingsInfo = await getCurrentSettings();
    
    if (!brandInfo) {
      throw createValidationError("No brand configuration found. Please set up your business details first.");
    }
    
    if (!settingsInfo) {
      throw createValidationError("No settings configuration found. Please configure invoice settings first.");
    }
    
    // ────────────────────────────────────────────────────────────────────────
    // STEP 3: CALL RPC FUNCTION (ATOMIC TRANSACTION)
    // ────────────────────────────────────────────────────────────────────────
    // RPC function will:
    // - Fetch current brand/settings snapshots from database
    // - Embed them as JSONB in invoice row
    // - Embed line items as JSONB array
    // - Generate invoice number with counter increment
    // - All in single atomic transaction
    
    const { data: result, error: txError } = await supabase.rpc('create_invoice_atomic', {
      p_invoice_data: {
        inv_date: validatedData.invDate,
        customer: validatedData.customer,
        tx_type: validatedData.txType || null,
        lines: validatedData.lines,
        subtotal: validatedData.subtotal || 0,
        total_tax: validatedData.totalTax || 0,
        item_discount_total: validatedData.itemDiscountTotal || 0,
        overall_disc_amt: validatedData.overallDiscAmt || 0,
        overall_discount: validatedData.overallDiscount || { type: "pct", value: 0 },
        grand_total: validatedData.grandTotal || 0,
        notes: validatedData.notes || ""
      }
    });
    
    if (txError) {
      throw txError;
    }
    
    const invoiceId = result.invoice_id;
    
    // ────────────────────────────────────────────────────────────────────────
    // STEP 4: MARK CUSTOMER AS USED (NON-CRITICAL)
    // ────────────────────────────────────────────────────────────────────────
    
    if (validatedData.customer?.name) {
      try {
        const { searchCustomers, markCustomerAsUsed } = await import("./customers.js");
        const customers = await searchCustomers(validatedData.customer.name, { includeArchived: false });
        
        const exactMatch = customers.find(c => 
          c.name.toLowerCase().trim() === validatedData.customer.name.toLowerCase().trim()
        );
        
        if (exactMatch) {
          await markCustomerAsUsed(exactMatch.id);
        }
      } catch (customerError) {
        // Non-critical - don't fail invoice creation
        console.warn('Failed to mark customer as used:', customerError);
      }
    }
    
    // ────────────────────────────────────────────────────────────────────────
    // STEP 5: FETCH AND RETURN CREATED INVOICE
    // ────────────────────────────────────────────────────────────────────────
    
    const invoice = await getInvoice(invoiceId);
    
    console.log(
      `✓ Invoice created: ${invoice.invNo} (${invoiceId}) ` +
      `with ${validatedData.lines.length} line items`
    );
    
    return { success: true, invoice };
    
  } catch (error) {
    console.error("Failed to create invoice:", error);
    
    // Format validation errors nicely
    if (error.name === 'ValidationError') {
      return {
        success: false,
        error: error.message,
        validationErrors: error.errors
      };
    }
    
    return {
      success: false,
      error: error.message
    };
  }
}

/**
 * Edit invoice by creating replacement invoice
 *
 * EDIT WORKFLOW:
 * 1. Create new invoice with CURRENT brand/settings snapshots
 * 2. Transfer payments from old to new invoice
 * 3. Void old invoice with bidirectional linking
 * 4. Return new invoice (may be overpaid - UI handles refund workflow)
 *
 * ✅ UPDATED FOR NEW SCHEMA:
 * - Fetches current brand/settings (not snapshot IDs)
 * - RPC embeds current snapshots in new invoice
 *
 * @param {string} invoiceId - Original invoice ID to edit
 * @param {object} newInvoiceData - New invoice data
 * @param {number} expectedVersion - Version for optimistic locking
 * @returns {Promise<object>} { success: boolean, invoice?: object, overpaid?: number }
 */
export async function updateInvoice(invoiceId, newInvoiceData, expectedVersion, markAsRefunded = false) {
  // ✅ PERMISSION CHECKS
  await requireFeature(FEATURES.CLOUD_DATABASE);
  await requireFeature(FEATURES.INVOICE_EDITING);
  
  try {
    // ────────────────────────────────────────────────────────────────────────
    // STEP 1: VALIDATE INPUT
    // ────────────────────────────────────────────────────────────────────────
    
    const validatedData = validateOrThrow(invoiceSchema, newInvoiceData);
    const userId = await getCurrentUserId();
    
    // ────────────────────────────────────────────────────────────────────────
    // STEP 2: CHECK ORIGINAL INVOICE EXISTS AND CAN BE EDITED
    // ────────────────────────────────────────────────────────────────────────
    
    const { data: originalInvoice, error: fetchError } = await supabase
      .from('invoices')
      .select('id, inv_no, version, status')
      .eq('user_id', userId)
      .eq('id', invoiceId)
      .single();
    
    if (fetchError || !originalInvoice) {
      throw new Error("Invoice not found");
    }
    
    if (originalInvoice.status === 'deleted') {
      throw new Error('Cannot edit deleted invoice');
    }
    
    if (originalInvoice.status === 'void') {
      throw new Error('Cannot edit voided invoice');
    }
    
    // ────────────────────────────────────────────────────────────────────────
    // STEP 3: OPTIMISTIC LOCKING - VERSION CHECK
    // ────────────────────────────────────────────────────────────────────────
    
    if (expectedVersion !== undefined && expectedVersion !== originalInvoice.version) {
      throw createValidationError(
        `Invoice was modified by another user/tab. Please refresh and try again.`,
        'version'
      );
    }
    
    // ────────────────────────────────────────────────────────────────────────
    // STEP 4: VERIFY CURRENT BRAND/SETTINGS EXIST
    // ────────────────────────────────────────────────────────────────────────
    
    const { getCurrentBrandInfo } = await import("./brand.js");
    const { getCurrentSettings } = await import("./settings.js");
    
    const brandInfo = await getCurrentBrandInfo({ hydrate: false });
    const settingsInfo = await getCurrentSettings();
    
    if (!brandInfo || !settingsInfo) {
      throw new Error("Missing brand or settings configuration");
    }
    
    // ────────────────────────────────────────────────────────────────────────
    // STEP 5: CALL RPC FUNCTION (ATOMIC TRANSACTION)
    // ────────────────────────────────────────────────────────────────────────
    // RPC function will:
    // - Fetch current brand/settings and embed in new invoice
    // - Create new invoice with current snapshots
    // - Transfer all payments from old to new
    // - Void old invoice
    // - All atomically in single transaction
    
    const { data: result, error: txError } = await supabase.rpc('edit_invoice_atomic', {
  p_original_invoice_id: invoiceId,
  p_original_version: originalInvoice.version,
  p_new_invoice_data: {
    inv_date: validatedData.invDate,
    customer: validatedData.customer,
    tx_type: validatedData.txType || null,
    lines: validatedData.lines,
    subtotal: validatedData.subtotal || 0,
    total_tax: validatedData.totalTax || 0,
    item_discount_total: validatedData.itemDiscountTotal || 0,
    overall_disc_amt: validatedData.overallDiscAmt || 0,
    overall_discount: validatedData.overallDiscount || { type: "pct", value: 0 },
    grand_total: validatedData.grandTotal || 0,
    notes: validatedData.notes || ""
  },
  p_mark_as_refunded: markAsRefunded  // ← ADD THIS LINE
});
    
    if (txError) {
      throw txError;
    }
    
    // ────────────────────────────────────────────────────────────────────────
    // STEP 6: FETCH AND RETURN NEW INVOICE
    // ────────────────────────────────────────────────────────────────────────
    
    const newInvoice = await getInvoice(result.new_invoice_id);
    const overpaid = result.overpayment_amount || 0;
    
    console.log(
      `✓ Invoice edited: ${originalInvoice.inv_no} → ${newInvoice.invNo} ` +
      `(${result.new_invoice_id})${overpaid > 0 ? ` - Overpaid by $${overpaid/100}` : ''}`
    );
    
    return {
      success: true,
      invoice: newInvoice,
      originalInvoiceId: invoiceId,
      overpaid: overpaid,
      message: overpaid > 0 
        ? `Invoice edited successfully. Customer overpaid by $${(overpaid/100).toFixed(2)}.`
        : 'Invoice edited successfully.'
    };
    
  } catch (error) {
    console.error("Failed to edit invoice:", error);
    
    if (error.name === 'ValidationError') {
      return {
        success: false,
        error: error.message,
        validationErrors: error.errors
      };
    }
    
    return {
      success: false,
      error: error.message,
      conflict: error.message.includes('modified by another user')
    };
  }
}

// ══════════════════════════════════════════════════════════════════════════════
// READ OPERATIONS
// ══════════════════════════════════════════════════════════════════════════════

/**
 * Get invoice by ID
 *
 * ✅ UPDATED FOR NEW SCHEMA:
 * - Single database query (no JOINs)
 * - Lines embedded in invoice.lines JSONB column
 * - Brand/settings snapshots embedded as JSONB
 * - 3-5x faster than old approach
 *
 * @param {string} invoiceId
 * @returns {Promise<object|null>}
 */
export async function getInvoice(invoiceId) {
  // ✅ PERMISSION CHECK
  await requireFeature(FEATURES.CLOUD_DATABASE);
  
  const userId = await getCurrentUserId();
  
  // ✅ SINGLE QUERY: Everything embedded in one row
  const { data: invoice, error } = await supabase
    .from('invoices')
    .select('*')
    .eq('user_id', userId)
    .eq('id', invoiceId)
    .single();
  
  if (error || !invoice) return null;
  
  // Convert to camelCase (lines already embedded in invoice.lines)
  return convertInvoiceToCamelCase(invoice);
}

/**
 * Get invoice by invoice number
 *
 * @param {string} invNo
 * @returns {Promise<object|null>}
 */
export async function getInvoiceByNumber(invNo) {
  // ✅ PERMISSION CHECK
  await requireFeature(FEATURES.CLOUD_DATABASE);
  
  const userId = await getCurrentUserId();
  
  const { data: invoice, error } = await supabase
    .from('invoices')
    .select('*')
    .eq('user_id', userId)
    .eq('inv_no', invNo)
    .single();
  
  if (error || !invoice) return null;
  
  // Convert to camelCase
  return convertInvoiceToCamelCase(invoice);
}

/**
 * Get all invoices
 *
 * ✅ UPDATED FOR NEW SCHEMA:
 * - Lines always included (embedded in JSONB)
 * - No separate "with lines" function needed
 * - Single query for all invoices
 *
 * @param {object} filters - { status, fromDate, toDate, includeDeleted, includeVoided, limit, offset }
 * @returns {Promise<Array>}
 */
export async function getAllInvoices(filters = {}) {
  // ✅ PERMISSION CHECK
  await requireFeature(FEATURES.CLOUD_DATABASE);
  
  const {
    status,
    fromDate,
    toDate,
    includeDeleted = false,
    includeVoided = false,
    limit,
    offset = 0
  } = filters;
  
  const userId = await getCurrentUserId();
  
  let query = supabase
    .from('invoices')
    .select('*')
    .eq('user_id', userId)
    .order('timestamp', { ascending: false });
  
  // Apply filters
  if (status) {
    query = query.eq('status', status);
  } else {
    // Default filtering
    let statusFilter = ['active'];
    if (includeVoided) statusFilter.push('void');
    if (includeDeleted) statusFilter.push('deleted');
    
    query = query.in('status', statusFilter);
  }
  
  if (fromDate) {
    query = query.gte('inv_date', fromDate);
  }
  
  if (toDate) {
    query = query.lte('inv_date', toDate);
  }
  
  // Apply pagination
  if (limit !== undefined) {
    query = query.range(offset, offset + limit - 1);
  }
  
  const { data: invoices, error } = await query;
  
  if (error) throw error;
  
  // Convert all invoices (lines already embedded)
  return invoices ? invoices.map(convertInvoiceToCamelCase) : [];
}

// ══════════════════════════════════════════════════════════════════════════════
// UPDATE OPERATIONS
// ══════════════════════════════════════════════════════════════════════════════

/**
 * Check if invoice can be edited
 * Returns payment status to help UI decide
 *
 * @param {string} invoiceId
 * @returns {Promise<object>}
 */
export async function canEditInvoice(invoiceId) {
  // ✅ PERMISSION CHECK
  await requireFeature(FEATURES.CLOUD_DATABASE);
  
  const { getInvoicePayments, getTotalPaid } = await import("./payments.js");
  
  const payments = await getInvoicePayments(invoiceId);
  const totalPaid = await getTotalPaid(invoiceId);
  
  return {
    hasPayments: payments.length > 0,
    totalPaid,
    paymentCount: payments.length,
    payments: payments.slice(0, 5) // Return first 5 for UI preview
  };
}

// ══════════════════════════════════════════════════════════════════════════════
// DELETE OPERATIONS (WITH CASCADE)
// ══════════════════════════════════════════════════════════════════════════════

/**
 * Soft delete invoice with CASCADE delete of related records
 *
 * ✅ USES RPC FUNCTION (Phase 2) - No invoice_lines to delete anymore
 *
 * @param {string} invoiceId
 * @param {object} options - { markPaymentsAsRefunded: boolean }
 * @returns {Promise<void>}
 */
export async function deleteInvoice(invoiceId, options = {}) {
  // ✅ PERMISSION CHECK
  await requireFeature(FEATURES.CLOUD_DATABASE);
  
  const { markPaymentsAsRefunded = true } = options;
  
  // Use PostgreSQL function for atomic cascade delete
  const { error } = await supabase.rpc('delete_invoice_cascade', {
    p_invoice_id: invoiceId,
    p_mark_payments_as_refunded: markPaymentsAsRefunded
  });
  
  if (error) throw error;
  
  console.log(`✓ Invoice deleted: ${invoiceId} (cascade complete)`);
}

/**
 * Restore deleted invoice with CASCADE restore
 *
 * ✅ USES RPC FUNCTION (Phase 2)
 *
 * @param {string} invoiceId
 */
export async function restoreInvoice(invoiceId) {
  // ✅ PERMISSION CHECKS
  await requireFeature(FEATURES.CLOUD_DATABASE);
  await requireFeature(FEATURES.INVOICE_RESTORE);
  
  // Use PostgreSQL function for atomic cascade restore
  const { error } = await supabase.rpc('restore_invoice_cascade', {
    p_invoice_id: invoiceId
  });
  
  if (error) throw error;
  
  console.log(`✓ Invoice restored: ${invoiceId}`);
}

/**
 * Permanently delete invoice and all related data
 * USE WITH EXTREME CAUTION - This cannot be undone!
 *
 * ✅ USES RPC FUNCTION (Phase 2)
 *
 * @param {string} invoiceId
 * @returns {Promise<void>}
 */
export async function permanentlyDeleteInvoice(invoiceId) {
  // ✅ PERMISSION CHECK
  await requireFeature(FEATURES.CLOUD_DATABASE);
  
  const userId = await getCurrentUserId();
  
  const { data: invoice } = await supabase
    .from('invoices')
    .select('inv_no')
    .eq('user_id', userId)
    .eq('id', invoiceId)
    .single();
  
  if (!invoice) {
    throw new Error("Invoice not found");
  }
  
  // Use PostgreSQL function for permanent cascade delete
  const { error } = await supabase.rpc('permanently_delete_invoice', {
    p_invoice_id: invoiceId
  });
  
  if (error) throw error;
  
  console.warn(`⚠️  Invoice ${invoice.inv_no} permanently deleted`);
}

// ══════════════════════════════════════════════════════════════════════════════
// HELPER FUNCTIONS
// ══════════════════════════════════════════════════════════════════════════════

/**
 * Convert database invoice (snake_case) to app format (camelCase)
 *
 * ✅ UPDATED FOR NEW SCHEMA:
 * - Removed: brandSnapshotId, settingsSnapshotId (old foreign keys)
 * - Added: brandSnapshot, settingsSnapshot (embedded JSONB)
 * - Removed: totalItems, totalQty (calculated from lines array now)
 * - Lines are now JSONB array (already embedded)
 *
 * @private
 */
function convertInvoiceToCamelCase(invoice) {
  if (!invoice) return null;
  
  // Parse lines from JSONB array and convert to camelCase
  const lines = Array.isArray(invoice.lines) 
    ? invoice.lines.map(convertLineItemToCamelCase)
    : [];
  
  return {
    id: invoice.id,
    invNo: invoice.inv_no,
    counter: invoice.counter,
    invDate: invoice.inv_date,
    timestamp: invoice.timestamp,
    version: invoice.version || 1,
    
    // ✅ NEW: Embedded snapshots (JSONB)
    brandSnapshot: invoice.brand_snapshot,
    settingsSnapshot: invoice.settings_snapshot,
    
    // Edit tracking
    replacesInvoiceId: invoice.replaces_invoice_id,
    replacesInvoiceNo: invoice.replaces_invoice_no,
    replacedByInvoiceId: invoice.replaced_by_invoice_id,
    replacedByInvoiceNo: invoice.replaced_by_invoice_no,
    
    // Customer snapshot
    customer: invoice.customer,
    
    // Transaction type
    txType: invoice.tx_type,
    
    // Totals
    subtotal: invoice.subtotal,
    totalTax: invoice.total_tax,
    itemDiscountTotal: invoice.item_discount_total,
    overallDiscAmt: invoice.overall_disc_amt,
    overallDiscount: invoice.overall_discount,
    grandTotal: invoice.grand_total,
    
    // Status
    status: invoice.status,
    voidedAt: invoice.voided_at,
    voidReason: invoice.void_reason,
    deletedAt: invoice.deleted_at,
    
    // Notes
    notes: invoice.notes,
    
    // Metadata
    updatedAt: invoice.updated_at,
    
    // ✅ NEW: Lines embedded as array (already converted to camelCase above)
    lines
  };
}

/**
 * Convert line item from JSONB to camelCase
 *
 * ✅ UPDATED: Now converts from JSONB object (not database row)
 * Line items are stored as JSON objects in the lines array
 *
 * @private
 */
function convertLineItemToCamelCase(line) {
  if (!line) return null;
  
  // Handle both database format (snake_case) and JSON format (camelCase/snake_case mix)
  return {
    lineNo: line.lineNo || line.line_no,
    
    // Product data
    name: line.name,
    productId: line.productId || line.product_id || '',
    hsn: line.hsn || '',
    barcode: line.barcode || '',
    unit: line.unit || '',
    
    // Quantities and pricing
    qty: line.qty,
    price: line.price,
    base: line.base,
    
    // Tax
    tax: line.tax || 0,
    tax2: line.tax2 || 0,
    
    // Discounts
    itemDiscount: line.itemDiscount || line.item_discount || { type: "pct", value: 0 },
    discAmt: line.discAmt || line.disc_amt || 0,
    afterDisc: line.afterDisc || line.after_disc || 0,
    
    // Tax breakdown
    taxTotal: line.taxTotal || line.tax_total || 0,
    sgstAmt: line.sgstAmt || line.sgst_amt || 0,
    cgstAmt: line.cgstAmt || line.cgst_amt || 0,
    igstAmt: line.igstAmt || line.igst_amt || 0,
    otherAmt: line.otherAmt || line.other_amt || 0,
    other2Amt: line.other2Amt || line.other2_amt || 0,
    split: line.split || { igst: 0, sgst: 0, cgst: 0, other: 0, other2: 0 },
    
    // Total
    total: line.total,
    
    // Custom fields
    customField1Value: line.customField1Value || line.custom_field1_value || '',
    customField2Value: line.customField2Value || line.custom_field2_value || ''
  };
}

// ══════════════════════════════════════════════════════════════════════════════
// BATCH DELETION OPERATIONS (NEW - RIGHT TO BE FORGOTTEN)
// ══════════════════════════════════════════════════════════════════════════════

/**
 * Delete all active invoices (soft delete with batch tracking)
 * Creates a deletion batch for 30-day recovery
 * 
 * ✅ PRODUCTION ROBUST:
 * - ACID transaction (RPC)
 * - Atomic batch creation + invoice/payment updates
 * - All-or-nothing deletion
 * - RLS enforced
 * 
 * @returns {Promise<object>} Batch info with deletion details
 */
export async function deleteAllInvoices() {
  // ✅ PERMISSION CHECK
  await requireFeature(FEATURES.RIGHT_TO_BE_FORGOTTEN);
  
  try {
    const { data, error } = await supabase.rpc('delete_invoice_batch');
    
    if (error) throw error;
    
    console.warn(
      `🗑️  SOFT DELETE: ${data.invoice_count} invoice(s) deleted. ` +
      `Recovery available until ${new Date(data.deletion_scheduled_at).toLocaleDateString()}`
    );
    
    return {
      success: true,
      batchId: data.batch_id,
      invoiceCount: data.invoice_count,
      paymentCount: data.payment_count,
      deletedAt: data.deleted_at,
      scheduledDeletionAt: data.deletion_scheduled_at,
      daysUntilPermanent: data.days_until_permanent,
      message: data.message
    };
  } catch (error) {
    console.error('Failed to delete invoices:', error);
    throw new Error(`Failed to delete invoices: ${error.message}`);
  }
}

/**
 * Restore a deleted invoice batch
 * 
 * ✅ PRODUCTION ROBUST:
 * - ACID transaction (RPC)
 * - Validates batch ownership & expiration
 * - Atomic restore of invoices + payments
 * - Prevents double-restore
 * 
 * @param {string} batchId - Deletion batch ID
 * @returns {Promise<object>} Restoration result
 */
export async function restoreInvoiceBatch(batchId) {
  // ✅ PERMISSION CHECK
  await requireFeature(FEATURES.RIGHT_TO_BE_FORGOTTEN);
  
  if (!batchId) {
    throw createValidationError('Batch ID is required', 'batchId');
  }
  
  try {
    const { data, error } = await supabase.rpc('restore_invoice_batch', {
      p_batch_id: batchId
    });
    
    if (error) throw error;
    
    // Check if RPC returned error in response (validation errors)
    if (!data.success) {
      throw new Error(data.error);
    }
    
    console.log(
      `✓ RESTORED: ${data.invoice_count} invoice(s), ${data.payment_count} payment(s)`
    );
    
    return {
      success: true,
      batchId: data.batch_id,
      invoiceCount: data.invoice_count,
      paymentCount: data.payment_count,
      restoredAt: data.restored_at,
      message: data.message
    };
  } catch (error) {
    console.error('Failed to restore invoice batch:', error);
    throw new Error(`Failed to restore: ${error.message}`);
  }
}

/**
 * Get all deletion batches for current user
 * 
 * ✅ PRODUCTION ROBUST:
 * - Single query (consistent snapshot)
 * - Calculated fields (days_remaining, status flags)
 * - RLS enforced
 * 
 * @returns {Promise<Array>} List of deletion batches with status
 */
export async function getDeletionBatches() {
  // ✅ PERMISSION CHECK
  await requireFeature(FEATURES.RIGHT_TO_BE_FORGOTTEN);
  
  try {
    const { data, error } = await supabase.rpc('get_deletion_batches');
    
    if (error) throw error;
    
    // Convert to camelCase
    return (data || []).map(batch => ({
      batchId: batch.batch_id,
      deletedAt: batch.deleted_at,
      deletionScheduledAt: batch.deletion_scheduled_at,
      invoiceCount: batch.invoice_count,
      restoredAt: batch.restored_at,
      restoreCount: batch.restore_count,
      permanentDeletionAt: batch.permanent_deletion_at,
      daysRemaining: batch.days_remaining,
      isExpired: batch.is_expired,
      isRestored: batch.is_restored,
      isPermanentlyDeleted: batch.is_permanently_deleted
    }));
  } catch (error) {
    console.error('Failed to get deletion batches:', error);
    throw error;
  }
}

/**
 * Manually trigger cleanup of expired batches
 * Usually run by cron job (pg_cron), but can be called manually
 * 
 * ✅ PRODUCTION ROBUST:
 * - Single transaction wraps all batch deletions
 * - Index-optimized (no full table scans)
 * - All-or-nothing cleanup
 * - Safe to run multiple times (idempotent)
 * 
 * @returns {Promise<object>} Cleanup summary
 */
export async function cleanupExpiredInvoiceBatches() {
  // ✅ PERMISSION CHECK
  await requireFeature(FEATURES.RIGHT_TO_BE_FORGOTTEN);
  
  try {
    const { data, error } = await supabase.rpc('cleanup_expired_invoice_batches');
    
    if (error) throw error;
    
    if (data.batches_deleted > 0) {
      console.warn(
        `⚠️  CLEANUP: ${data.batches_deleted} batch(es) permanently deleted ` +
        `(${data.invoices_deleted} invoices, ${data.payments_deleted} payments)`
      );
    } else {
      console.log('✓ No expired batches to clean up');
    }
    
    return {
      success: true,
      batchesDeleted: data.batches_deleted,
      invoicesDeleted: data.invoices_deleted,
      paymentsDeleted: data.payments_deleted,
      batches: data.batches,
      message: data.message
    };
  } catch (error) {
    console.error('Failed to cleanup expired batches:', error);
    throw error;
  }
}

/**
 * EXPORTED FUNCTIONS (13 total):
 *
 * ✅ Create:
 * - createInvoice(data) - Updated to embed snapshots
 * - updateInvoice(id, data, version) - Updated to embed snapshots
 *
 * ✅ Read:
 * - getInvoice(id) - Single query, lines embedded
 * - getInvoiceByNumber(invNo) - Single query
 * - getAllInvoices(filters) - Lines always included
 *
 * ✅ Update:
 * - canEditInvoice(id) - Check payment status
 *
 * ✅ Delete:
 * - deleteInvoice(id, options) - Soft delete with cascade
 * - restoreInvoice(id) - Restore with cascade
 * - permanentlyDeleteInvoice(id) - Hard delete (CAUTION)
 *
 * ✅ Batch Deletion (NEW - RIGHT TO BE FORGOTTEN):
 * - deleteAllInvoices() - Soft delete all invoices (30-day recovery)
 * - restoreInvoiceBatch(batchId) - Restore specific deletion batch
 * - getDeletionBatches() - Get all batches with status
 * - cleanupExpiredInvoiceBatches() - Permanent cleanup (manual/cron)
 *
 * ═══════════════════════════════════════════════════════════════════════════
 */